import type { ColumnType, ListFilter, ListSort } from "@patronix/domain/list";

// Column filters and sort for lists that read a table through PostgREST + RLS
// (docs/design/column-filters.md §4): the same filter model as the list RPCs,
// mapped to PostgREST operators, with a keyset cursor on (sort column, id).
// Only plain columns of the table; embedded resources are shown, not
// filtered. Values reach PostgREST as quoted literals.

export interface RestColumn {
  /** List column key (spec). */
  key: string;
  /** Table column. */
  column: string;
  type: ColumnType;
  /** The column can be null (keyset continues into the trailing nulls). */
  nullable?: boolean;
  /** A timestamptz column filtered by calendar day (the viewer's local day). */
  timestamp?: boolean;
}

/** The part of a PostgREST filter builder the helper uses. */
export interface RestQuery<Q> {
  eq(column: string, value: unknown): Q;
  neq(column: string, value: unknown): Q;
  gt(column: string, value: unknown): Q;
  gte(column: string, value: unknown): Q;
  lt(column: string, value: unknown): Q;
  lte(column: string, value: unknown): Q;
  ilike(column: string, pattern: string): Q;
  in(column: string, values: readonly unknown[]): Q;
  is(column: string, value: boolean | null): Q;
  not(column: string, operator: string, value: unknown): Q;
  or(filters: string): Q;
  order(column: string, options?: { ascending?: boolean; nullsFirst?: boolean }): Q;
}

/** Text in a LIKE pattern: wildcards typed by the user are literal. */
const likeEscape = (s: string) => s.replace(/[\\%_]/g, (c) => `\\${c}`);
/** A value inside or(...): double-quoted, with quotes and backslashes escaped. */
const orValue = (v: string) => `"${v.replace(/["\\]/g, (c) => `\\${c}`)}"`;

/** Start of a local calendar day as an ISO instant ("2026-10-22" → local midnight). */
function dayStart(day: string, plusDays = 0): string {
  const [y, m, d] = day.split("-").map(Number) as [number, number, number];
  return new Date(y, m - 1, d + plusDays).toISOString();
}

export function applyRestFilters<Q extends RestQuery<Q>>(query: Q, columns: readonly RestColumn[], filters: readonly ListFilter[]): Q {
  let q = query;
  for (const f of filters) {
    const col = columns.find((c) => c.key === f.c);
    if (!col) continue;
    const c = col.column;
    if (f.o === "empty") {
      q = col.type === "text" ? q.or(`${c}.is.null,${c}.eq.""`) : q.is(c, null);
      continue;
    }
    if (f.o === "notempty") {
      q = q.not(c, "is", null);
      if (col.type === "text") q = q.neq(c, "");
      continue;
    }
    if (col.type === "text" && typeof f.v === "string") {
      if (f.o === "contains") q = q.ilike(c, `%${likeEscape(f.v)}%`);
      else if (f.o === "starts") q = q.ilike(c, `${likeEscape(f.v)}%`);
      else if (f.o === "eq") q = q.ilike(c, likeEscape(f.v));
    } else if ((col.type === "number" || col.type === "date") && f.v !== undefined) {
      const [a, b] = Array.isArray(f.v) ? [...f.v].sort((x, y) => (col.type === "number" ? Number(x) - Number(y) : x < y ? -1 : 1)) : [f.v as string, f.v as string];
      if (col.timestamp) {
        // A day is [local midnight, next local midnight).
        if (f.o === "eq" || f.o === "between") q = q.gte(c, dayStart(a!)).lt(c, dayStart(b!, 1));
        else if (f.o === "ge") q = q.gte(c, dayStart(a!));
        else if (f.o === "le") q = q.lt(c, dayStart(a!, 1));
      } else if (f.o === "eq") q = q.eq(c, a);
      else if (f.o === "ge") q = q.gte(c, a);
      else if (f.o === "le") q = q.lte(c, a);
      else if (f.o === "between") q = q.gte(c, a).lte(c, b);
    } else if (col.type === "enum" && Array.isArray(f.v)) {
      q = q.in(c, f.v);
    } else if (col.type === "bool" && typeof f.v === "boolean") {
      q = q.eq(c, f.v);
    }
  }
  return q;
}

export interface RestCursor {
  /** Sort column value of the last row (absent when sorting by id). */
  v?: string | number | boolean | null;
  /** id of the last row. */
  t: string;
}

/**
 * Order + keyset for one page. Without a sort the list's default applies
 * (`fallback`). Sorting is by one column then id, nulls last.
 */
export function applyRestSort<Q extends RestQuery<Q>>(
  query: Q,
  columns: readonly RestColumn[],
  sort: ListSort | null,
  fallback: ListSort,
  cursor: RestCursor | null,
): Q {
  const s = sort ?? fallback;
  const col = columns.find((c) => c.key === s.col);
  if (!col) return query;
  const asc = s.dir === "asc";
  let q = query.order(col.column, { ascending: asc, nullsFirst: false }).order("id", { ascending: asc });
  if (cursor) {
    const cmp = asc ? "gt" : "lt";
    const c = col.column;
    const t = orValue(cursor.t);
    if (cursor.v === null || cursor.v === undefined) {
      // Already in the trailing nulls (or sorting by id): only more of them, by id.
      if (col.nullable) q = q.is(c, null);
      q = asc ? q.gt("id", cursor.t) : q.lt("id", cursor.t);
    } else {
      const v = orValue(String(cursor.v));
      q = q.or(`${c}.${cmp}.${v},and(${c}.eq.${v},id.${cmp}.${t})${col.nullable ? `,${c}.is.null` : ""}`);
    }
  }
  return q;
}

/** Cursor of the last row of a page (stringified for the infinite query). */
export function restCursorOf(row: Record<string, unknown>, columns: readonly RestColumn[], sort: ListSort | null, fallback: ListSort): string {
  const s = sort ?? fallback;
  const col = columns.find((c) => c.key === s.col);
  const v = col ? (row[col.column] as RestCursor["v"]) : undefined;
  return JSON.stringify({ v: v ?? null, t: String(row.id) } satisfies RestCursor);
}

/**
 * A PostgREST builder seen only through the methods above, for tables whose
 * generated select types are too deep for the generic helpers.
 */
export interface LooseQuery extends RestQuery<LooseQuery> {
  limit(count: number): LooseQuery;
  abortSignal(signal: AbortSignal): PromiseLike<{ data: unknown; error: Error | null }>;
}
